import { useParams, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import SEO from "@/components/base/SEO";
import { comics, categories } from "@/mocks/comics";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const sort = searchParams.get("sort") || "latest";

  const category = slug === "all" ? null : categories.find((c) => c.slug === slug);
  const categoryName = category ? category.name : "全部漫画";

  let filteredComics = slug === "all" ? comics : comics.filter((c) => {
    const catMap: Record<string, string> = {
      romance: "恋爱",
      fantasy: "奇幻",
      action: "动作",
      thriller: "悬疑",
      "slice-of-life": "日常",
      comedy: "搞笑",
      historical: "古风",
      horror: "恐怖",
    };
    return c.category === (catMap[slug || ""] || slug);
  });

  if (sort === "popular") {
    filteredComics = [...filteredComics].sort((a, b) => b.rating - a.rating);
  }

  const breadcrumbs = [
    { label: "首页", to: "/" },
    { label: categoryName, to: `/category/${slug}` },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.label,
          item: `http://jiarenmei.com/${item.to}`,
        })),
      },
      {
        "@type": "CollectionPage",
        name: `韩漫画网 - ${categoryName}`,
        description: `韩漫画网${categoryName}分类，高清免费韩国漫画在线阅读。`,
        url: `http://jiarenmei.com/category/${slug}`,
        isPartOf: { "@id": "http://jiarenmei.com/#website" },
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${categoryName} - 韩漫画网高清免费韩国漫画在线阅读`}
        description={`韩漫画网${categoryName}分类提供高清免费韩国漫画在线阅读，内容精彩画质清晰。涵盖恋爱、奇幻、动作、悬疑等多种题材，每日更新。`}
        canonicalUrl={`http://jiarenmei.com/category/${slug}`}
        jsonLd={jsonLd}
        type="website"
      />
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
              {breadcrumbs.map((item, index) => (
                <span key={item.to} className="flex items-center gap-2">
                  {index > 0 && (
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </div>
                  )}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="text-foreground-900 font-medium">{item.label}</span>
                  ) : (
                    <Link to={item.to} className="hover:text-primary-500 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </span>
              ))}
            </nav>

            <h1 className="text-2xl md:text-4xl font-extrabold text-foreground-900 mb-2">
              {categoryName}
              <span className="font-heading italic text-primary-500 ml-2">漫画</span>
            </h1>
            <p className="text-sm text-foreground-500">
              {category
                ? `共收录${category.count}部${categoryName}题材韩国漫画，持续更新中`
                : `共收录${comics.length}部韩国漫画，每日更新持续连载`}
            </p>
          </div>
        </section>

        <section className="py-8 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <Link
                  to={`/category/${slug}?sort=latest`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sort === "latest"
                      ? "bg-foreground-900 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  最新更新
                </Link>
                <Link
                  to={`/category/${slug}?sort=popular`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sort === "popular"
                      ? "bg-foreground-900 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  热门排行
                </Link>
              </div>
              <span className="text-xs text-foreground-500">{filteredComics.length} 部作品</span>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                {filteredComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    author={comic.author}
                    coverImage={comic.coverImage}
                    rating={comic.rating}
                    status={comic.status}
                    chapters={comic.chapters}
                    variant="default"
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-book-open-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的漫画作品</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-900">
                  编辑推荐
                  <span className="text-primary-500 ml-2">· 精选佳作</span>
                </h2>
                <p className="text-sm text-foreground-500 mt-1.5">编辑团队精心挑选的高质量漫画</p>
              </div>
              <span className="hidden md:inline-block px-4 py-1.5 rounded-full bg-foreground-900 text-background-50 text-xs font-medium">
                编辑精选
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {comics.filter((c) => c.rating >= 9.4).slice(0, 4).map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  coverImage={comic.coverImage}
                  rating={comic.rating}
                  status={comic.status}
                  chapters={comic.chapters}
                  variant="default"
                />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}